/**
 * Platform Strategy API Service
 * 
 * Service for connecting to the backend platform strategy endpoints implemented in PX-181.
 * Handles platform-specific recommendations, optimal timing, and cross-platform insights.
 */

import { createApiService, responseTransformers } from './apiUtils';

// Create specialized API service for platform strategy
const platformStrategyApiService = createApiService('PlatformStrategy', '/api/analytics/strategies');

/**
 * Transform backend strategy data to frontend format
 * @param {Object} backendData - Strategy data from backend
 * @returns {Object} Transformed strategy data for frontend
 */
const transformStrategyData = (backendData) => {
  if (!backendData) return null;

  const camelCased = responseTransformers.toCamelCase(backendData);
  
  return {
    id: camelCased.id,
    platform: camelCased.platform,
    strategyType: camelCased.strategyType,
    recommendations: camelCased.recommendations || [],
    optimalTiming: camelCased.optimalTiming ? {
      timeZone: camelCased.optimalTiming.timeZone,
      bestDays: camelCased.optimalTiming.bestDays || [],
      bestHours: camelCased.optimalTiming.bestHours || [],
      peakEngagementWindows: camelCased.optimalTiming.peakEngagementWindows || []
    } : null,
    contentGuidelines: camelCased.contentGuidelines ? {
      characterLimits: camelCased.contentGuidelines.characterLimits,
      hashtagRecommendations: camelCased.contentGuidelines.hashtagRecommendations || [],
      mediaRequirements: camelCased.contentGuidelines.mediaRequirements || {},
      toneRecommendations: camelCased.contentGuidelines.toneRecommendations || []
    } : null,
    performanceInsights: camelCased.performanceInsights ? {
      avgEngagementRate: camelCased.performanceInsights.avgEngagementRate || 0,
      bestPerformingContentTypes: camelCased.performanceInsights.bestPerformingContentTypes || [],
      audienceInsights: camelCased.performanceInsights.audienceInsights || {}
    } : null,
    lastUpdatedAt: camelCased.lastUpdatedAt,
    isActive: camelCased.isActive || false,
    confidenceScore: camelCased.confidenceScore || 0
  };
};

/**
 * Transform backend analysis data to frontend format
 * @param {Object} backendAnalysis - Analysis data from backend
 * @returns {Object} Transformed analysis data for frontend
 */
const transformAnalysisData = (backendAnalysis) => {
  if (!backendAnalysis) return null;

  const camelCased = responseTransformers.toCamelCase(backendAnalysis);
  
  return {
    id: camelCased.id,
    analysisType: camelCased.analysisType,
    platforms: camelCased.platforms || [],
    timeRange: camelCased.timeRange,
    insights: camelCased.insights || [],
    recommendations: camelCased.recommendations || [],
    crossPlatformOpportunities: camelCased.crossPlatformOpportunities || [],
    performanceComparison: camelCased.performanceComparison || {},
    createdAt: camelCased.createdAt,
    isCompleted: camelCased.isCompleted || false,
    processingStatus: camelCased.processingStatus || 'pending'
  };
};

/**
 * Platform Strategy API functions
 */
export const platformStrategyApi = {
  /**
   * Get platform strategies dashboard
   * @returns {Promise} Platform strategies overview
   */
  getDashboard: async () => {
    console.log('[Platform Strategy API] Fetching strategies dashboard');
    
    const response = await platformStrategyApiService.get('/dashboard/');
    return responseTransformers.single(response, (data) => {
      const camelCased = responseTransformers.toCamelCase(data);
      return {
        totalStrategies: camelCased.totalStrategies || 0,
        activeStrategies: camelCased.activeStrategies || 0,
        averagePerformanceBoost: camelCased.averagePerformanceBoost || 0,
        platformBreakdown: camelCased.platformBreakdown || [],
        recentAnalyses: camelCased.recentAnalyses || [],
        upcomingRecommendations: camelCased.upcomingRecommendations || []
      };
    });
  },

  /**
   * Get platform-specific strategy
   * @param {string} platform - Platform name
   * @returns {Promise} Platform strategy details
   */
  getPlatformStrategy: async (platform) => {
    console.log('[Platform Strategy API] Fetching strategy for platform:', platform);
    
    const response = await platformStrategyApiService.get(`/platforms/${platform}/`);
    return responseTransformers.single(response, transformStrategyData);
  },

  /**
   * Get all platform strategies for user
   * @param {Object} options - Query options
   * @returns {Promise} Platform strategies list
   */
  getAllStrategies: async (options = {}) => {
    console.log('[Platform Strategy API] Fetching all strategies:', options);
    
    const response = await platformStrategyApiService.get('/all/', {
      status: options.status,
      platform: options.platform,
      strategy_type: options.strategyType,
    });
    
    return responseTransformers.single(response, (data) => 
      (data.strategies || []).map(transformStrategyData)
    );
  },

  /**
   * Generate platform strategy analysis
   * @param {Object} analysisRequest - Analysis request data
   * @returns {Promise} Analysis result
   */
  generateAnalysis: async (analysisRequest) => {
    console.log('[Platform Strategy API] Generating platform analysis:', analysisRequest);
    
    const response = await platformStrategyApiService.post('/analyze/', {
      platforms: analysisRequest.platforms || [],
      time_range: analysisRequest.timeRange || '30d',
      analysis_type: analysisRequest.analysisType || 'comprehensive',
      include_recommendations: analysisRequest.includeRecommendations !== false
    });
    
    return responseTransformers.single(response, transformAnalysisData);
  },

  /**
   * Get analysis status and results
   * @param {string} analysisId - Analysis ID
   * @returns {Promise} Analysis details
   */
  getAnalysis: async (analysisId) => {
    console.log('[Platform Strategy API] Fetching analysis:', analysisId);
    
    const response = await platformStrategyApiService.get(`/analyses/${analysisId}/`);
    return responseTransformers.single(response, transformAnalysisData);
  },

  /**
   * Get optimal posting times analysis
   * @param {Object} options - Timing analysis options
   * @returns {Promise} Timing recommendations
   */
  getOptimalTiming: async (options = {}) => {
    console.log('[Platform Strategy API] Fetching optimal timing analysis:', options);
    
    const response = await platformStrategyApiService.get('/timing/', {
      platforms: options.platforms?.join(','),
      time_zone: options.timeZone,
      analysis_period: options.analysisPeriod || '90d',
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get content optimization recommendations
   * @param {Object} options - Content analysis options
   * @returns {Promise} Content recommendations
   */
  getContentRecommendations: async (options = {}) => {
    console.log('[Platform Strategy API] Fetching content recommendations:', options);
    
    const response = await platformStrategyApiService.get('/content-recommendations/', {
      platform: options.platform,
      content_type: options.contentType,
      target_audience: options.targetAudience,
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get cross-platform opportunities
   * @param {Object} options - Cross-platform analysis options
   * @returns {Promise} Cross-platform opportunities
   */
  getCrossPlatformOpportunities: async (options = {}) => {
    console.log('[Platform Strategy API] Fetching cross-platform opportunities:', options);
    
    const response = await platformStrategyApiService.get('/cross-platform/', {
      time_range: options.timeRange,
      min_opportunity_score: options.minOpportunityScore,
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Apply strategy recommendations
   * @param {string} strategyId - Strategy ID
   * @param {Array} selectedRecommendations - Selected recommendation IDs
   * @returns {Promise} Application result
   */
  applyRecommendations: async (strategyId, selectedRecommendations = []) => {
    console.log('[Platform Strategy API] Applying recommendations:', strategyId, selectedRecommendations);
    
    const response = await platformStrategyApiService.post(`/strategies/${strategyId}/apply/`, {
      recommendation_ids: selectedRecommendations
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Rate strategy effectiveness
   * @param {string} strategyId - Strategy ID
   * @param {Object} rating - Rating data
   * @returns {Promise} Rating result
   */
  rateStrategy: async (strategyId, rating) => {
    console.log('[Platform Strategy API] Rating strategy:', strategyId, rating);
    
    const response = await platformStrategyApiService.post(`/strategies/${strategyId}/rate/`, {
      effectiveness_score: rating.effectivenessScore,
      implementation_difficulty: rating.implementationDifficulty,
      feedback: rating.feedback || ''
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get strategy performance tracking
   * @param {string} strategyId - Strategy ID
   * @returns {Promise} Performance tracking data
   */
  getStrategyPerformance: async (strategyId) => {
    console.log('[Platform Strategy API] Fetching strategy performance:', strategyId);
    
    const response = await platformStrategyApiService.get(`/strategies/${strategyId}/performance/`);
    return responseTransformers.single(response);
  },

  /**
   * Update strategy settings
   * @param {string} strategyId - Strategy ID
   * @param {Object} updates - Strategy updates
   * @returns {Promise} Updated strategy
   */
  updateStrategy: async (strategyId, updates) => {
    console.log('[Platform Strategy API] Updating strategy:', strategyId, updates);
    
    const response = await platformStrategyApiService.patch(`/strategies/${strategyId}/`, updates);
    return responseTransformers.single(response, transformStrategyData);
  },

  /**
   * Delete strategy
   * @param {string} strategyId - Strategy ID
   * @returns {Promise} Deletion result
   */
  deleteStrategy: async (strategyId) => {
    console.log('[Platform Strategy API] Deleting strategy:', strategyId);
    
    await platformStrategyApiService.delete(`/strategies/${strategyId}/`);
    return { success: true };
  }
};

/**
 * Platform strategy tracking helper function
 * @param {string} eventName - Event name
 * @param {Object} properties - Event properties
 */
export const trackStrategyEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Strategy Tracking] ${eventName}:`, properties);
  }
  
  // Track event with analytics API
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track(`platform_strategy_${eventName}`, properties);
  }
};

export default platformStrategyApi; 